import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useToasts } from "react-toast-notifications";
import { useSidebarNavigation } from "../SidebarNavigation/useSidebarNavigation";

export const useReconDetails = () => {
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const { reconId } = useParams();
  const dispatch = useDispatch();
  const { authData, storeId, userData } = useSelector(
    (state) => state.loginSlice,
  );
  const { hasService } = useSidebarNavigation();
  const isMounted = useRef(true);

  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeOrderIndex, setActiveOrderIndex] = useState(0);
  const [activeMessageTab, setActiveMessageTab] = useState(0);

  // State for the new Issue Modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [isSubmittingIssue, setIsSubmittingIssue] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [toSubscriberId, setToSubscriberId] = useState("");
  const [toSubscriberUri, setToSubscriberUri] = useState("");

  const settlementStatusOptions = [
    "PENDING",
    "SETTLED",
    "TO BE INITIATED",
  ];

  const [showSendReconModal, setShowSendReconModal] = useState(false);
  const [isSubmittingSendRecon, setIsSubmittingSendRecon] = useState(false);
  const [sendReconForm, setSendReconForm] = useState({
    transaction_id: "",
    orders: [],
  });

  // State for raise issue form
  const [orderData, setOrderData] = useState(null);
  const [fetchingOrderData, setFetchingOrderData] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [raiseIssueFormData, setRaiseIssueFormData] = useState({
    domain: "",
    issue_category: "",
    issue_subcategory: "",
    bap_id: userData?.user_id || "",
    city: "",
    bap_uri: userData?.subscriber_id || "",
    refs: [{ ref_type: "PAYMENT", ref_id: "" }],
    short_desc: "",
    detailed_desc: "",
    images: [],
  });
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    isMounted.current = true;

    const fetchReconDetails = async () => {
      if (!reconId || !authData) return;

      setLoading(true);
      setError(null);

      try {
        const result = await apiHandler({
          url: `${endpoint.GET_RECONS_DETAIL}${reconId}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        const data = result?.data?.data;

        if (isMounted.current && data) {
          setTransactionData(data);
          setActiveOrderIndex(0);
          setActiveMessageTab(0);
        }
      } catch (err) {
        if (isMounted.current) {
          //console.error("Error fetching recon details:", err);
          setError("Failed to fetch recon details.");
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchReconDetails();

    return () => {
      isMounted.current = false;
    };
  }, [reconId, authData, dispatch]);

  const handleLatestSettlement = useCallback(
    async (e) => {
      e.preventDefault();

      if (!transactionData) {
        addToast("Transaction data not loaded yet.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const currentOrder = transactionData?.orders?.[activeOrderIndex];
      if (!currentOrder) {
        addToast("No order selected.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const {
        _id,
        buyer_app_id,
        buyer_app_uri,
        seller_app_id,
        seller_app_uri,
      } = currentOrder;

      if (
        !_id ||
        !buyer_app_id ||
        !buyer_app_uri ||
        !seller_app_id ||
        !seller_app_uri
      ) {
        //console.error("Invalid or incomplete order details", currentOrder);
        addToast(
          "Cannot send settlement request. Missing order or app details.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.SEND_SETTLEMENT_REQUEST}`;

        const payload = {
          order_ids: [_id],
          app_details: {
            buyer_app_id,
            buyer_app_uri,
            seller_app_id,
            seller_app_uri,
          },
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId,
          ) || [];

        let toastMessage =
          response?.data?.response?.data?.message ||
          "Settlement requested successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          },
        );
      } catch (error) {
        //console.error("API Error:", error);

        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, transactionData, activeOrderIndex, addToast, storeId],
  );

  const handleRaiseRecon = useCallback(
    async (e) => {
      e.preventDefault();

      if (!transactionData) {
        addToast("Transaction data not loaded yet.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const currentOrder = transactionData?.orders?.[activeOrderIndex];
      if (!currentOrder) {
        addToast("No order selected.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      const {
        _id,
        buyer_app_id,
        buyer_app_uri,
        seller_app_id,
        seller_app_uri,
      } = currentOrder;

      if (
        !_id ||
        !buyer_app_id ||
        !buyer_app_uri ||
        !seller_app_id ||
        !seller_app_uri
      ) {
        //console.error("Invalid or incomplete order details", currentOrder);
        addToast(
          "Cannot send settlement request. Missing order or app details.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
        return;
      }

      try {
        setLoading(true);

        const url = `${endpoint.RAISE_RECON}`;

        const payload = {
          order_ids: [_id],
          app_details: {
            buyer_app_id,
            buyer_app_uri,
            seller_app_id,
            seller_app_uri,
          },
          ...(transactionData?.orders?.[activeOrderIndex]?.domain && { domain: transactionData.orders?.[activeOrderIndex]?.domain }),
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const failedOrdersList =
          response?.data?.response?.data?.failedOrders?.filter(
            (item) => item && typeof item === "object" && item.orderId,
          ) || [];

        let toastMessage =
          response?.data?.response?.data?.message ||
          "Recon raised successfully";

        let failedOrdersContent = null;
        if (failedOrdersList.length > 0) {
          failedOrdersContent = (
            <div style={{ marginTop: "10px" }}>
              <strong>Failed Order:</strong>
              <ul style={{ marginTop: "5px", color: "#e74c3c" }}>
                {failedOrdersList.map((order, index) => (
                  <li key={index}>
                    <strong>Order ID:</strong> {order.orderId} <br />
                    <strong>Error:</strong> {order.error}
                  </li>
                ))}
              </ul>
            </div>
          );
        }

        addToast(
          <>
            <div>{toastMessage}</div>
            {failedOrdersContent}
          </>,
          {
            appearance: responseStatus === 200 ? "success" : "error",
            autoDismiss: true,
          },
        );
      } catch (error) {
        //console.error("API Error:", error);

        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, transactionData, activeOrderIndex, addToast, storeId],
  );

  const handleRaiseIssueSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!paymentId || !toSubscriberId || !toSubscriberUri) {
        addToast("All fields are required.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      if (!storeId) {
        addToast("Store ID (Tenant ID) not available. Cannot raise issue.", {
          appearance: "error",
          autoDismiss: true,
        });
        return;
      }

      setIsSubmittingIssue(true);

      try {
        const url = `${endpoint.RAISE_RECON_ISSUE}?`;

        const payload = {
          tenant_id: storeId,
          payment_id: paymentId,
          to_subscriber_id: toSubscriberId,
          to_subscriber_uri: toSubscriberUri,
        };

        const response = await apiHandler({
          url,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        // --- MODIFIED SECTION ---
        const responseData = response?.data;

        if (responseData?.status === 200 && responseData?.redirectUrl) {
          // Success: We have a redirect URL.
          addToast("Issue raised. Redirecting to IGM...", {
            appearance: "success",
            autoDismiss: true,
          });

          // Perform the browser redirect
          window.location.href = responseData.redirectUrl;

          // Page is navigating away, no need to close modal or stop spinner
        } else {
          // Handle cases where the API call succeeded but didn't return
          // a redirectUrl, or returned a different status in its body.
          const toastMessage =
            responseData?.message ||
            "Issue raised successfully, but no redirect was provided.";

          addToast(toastMessage, {
            appearance: responseData?.status === 200 ? "success" : "error",
            autoDismiss: true,
          });

          if (responseData?.status === 200) {
            setShowIssueModal(false); // Close modal only on non-redirect success
          }
        }
        // --- END OF MODIFIED SECTION ---
      } catch (error) {
        //console.error("API Error raising issue:", error);

        // This catch block handles network errors or HTTP 4xx/5xx responses
        addToast(
          error.response?.data?.message ||
          "Unexpected error occurred while raising issue.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      } finally {
        // This will only be executed if no redirect happens
        setIsSubmittingIssue(false);
      }
    },
    [
      paymentId,
      toSubscriberId,
      toSubscriberUri,
      authData,
      dispatch,
      storeId,
      addToast,
    ],
  );

  // New handlers for opening/closing the modal
  const handleOpenIssueModal = useCallback(() => {
    const currentOrder = transactionData?.orders?.[activeOrderIndex];
    if (currentOrder) {
      // Pre-populate fields based on the current order
      setToSubscriberId(currentOrder.buyer_app_id || "");
      setToSubscriberUri(currentOrder.buyer_app_uri || "");
    }
    setPaymentId(""); // Clear payment ID for user input
    setShowIssueModal(true);
  }, [transactionData, activeOrderIndex]);

  const handleCloseIssueModal = () => {
    setShowIssueModal(false);
  };

  const handleOpenSendReconModal = useCallback(() => {
    const orders = Array.isArray(transactionData?.orders)
      ? transactionData.orders
      : [];

    setSendReconForm({
      transaction_id:
        transactionData?.recon_transaction_id ||
        transactionData?.transaction_id ||
        reconId ||
        "",
      orders: orders.map((order) => {
        const reconMessage = order?.messages?.find(
          (msg) => msg?.recon_sent?.settlements?.length
        );

        return {
          order_id: order?.bId || order?.b_id || order?.order_id || "",
          settlement_id:
            order?.settlement_id ||
            reconMessage?.recon_sent?.settlements?.[0]?.settlement_id ||
            "",
          recon_accord: Boolean(order?.recon_accord),
          settlement_status:
            order?.settlement_status ||
            order?.settlementStatus ||
            "PENDING",
          settlement_amount:
            order?.settlement_amount ??
            order?.settled_amount ??
            "",
          settlement_commission:
            order?.settlement_commission ??
            order?.taxes_charges_summary?.buyer_finder_fee ??
            "",
        };
      }),
    });

    setShowSendReconModal(true);
  }, [transactionData, reconId]);

  const handleCloseSendReconModal = useCallback(() => {
    setShowSendReconModal(false);
  }, []);

  const handleSendReconFieldChange = useCallback((field, value) => {
    setSendReconForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSendReconOrderChange = useCallback((index, field, value) => {
    setSendReconForm((prev) => ({
      ...prev,
      orders: prev.orders.map((order, orderIndex) =>
        orderIndex === index ? { ...order, [field]: value } : order,
      ),
    }));
  }, []);

  const handleSendReconSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!sendReconForm.transaction_id?.trim()) {
        addToast("Transaction ID is required.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      if (!Array.isArray(sendReconForm.orders) || !sendReconForm.orders.length) {
        addToast("No orders available for recon submission.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      const invalidOrder = sendReconForm.orders.find((order) => {
        const amount = Number(order.settlement_amount);
        const commission = Number(order.settlement_commission);

        return (
          !order.order_id?.trim() ||
          !order.settlement_id?.trim() ||
          !order.settlement_status?.trim() ||
          !Number.isFinite(amount) ||
          amount <= 0 ||
          !Number.isFinite(commission) ||
          commission < 0
        );
      });

      if (invalidOrder) {
        addToast("Please complete all recon fields with valid values.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      try {
        setIsSubmittingSendRecon(true);

        const payload = {
          transaction_id: sendReconForm.transaction_id.trim(),
          orders: sendReconForm.orders.map((order) => ({
            order_id: order.order_id.trim(),
            recon_accord: Boolean(order.recon_accord),
            settlement_id: order.settlement_id.trim(),
            settlement_status:
              order.settlement_status.trim() === "TO BE INITIATED"
                ? "TO_BE_INITIATED"
                : order.settlement_status.trim(),
            settlement_amount: String(Number(order.settlement_amount)),
            settlement_commission: String(Number(order.settlement_commission)),
          })),
        };

        const response = await apiHandler({
          url: endpoint.SEND_ON_RECON_REQUEST,
          method: "POST",
          data: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            tenantid: storeId,
          },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseData = response?.data;
        const responseMessage =
          responseData?.message ||
          responseData?.data?.message ||
          "Recon submitted successfully.";

        addToast(responseMessage, {
          appearance:
            response?.status === 200 || responseData?.status === 200
              ? "success"
              : "error",
          autoDismiss: true,
        });

        if (response?.status === 200 || responseData?.status === 200) {
          setShowSendReconModal(false);
        }
      } catch (error) {
        addToast(
          error.response?.data?.message ||
          error.message ||
          "Failed to submit recon request.",
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      } finally {
        setIsSubmittingSendRecon(false);
      }
    },
    [addToast, authData, dispatch, sendReconForm, storeId],
  );

  // Form change handler for modal
  const handleFormChange = (e) => {
    const { id, value } = e.target;
    setRaiseIssueFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Category change handler
  const handleCategoryChange = (e) => {
    const categoryName = e.target.value;
    const categoryMapping = orderData?.igmData?.CategoryMapping || [];
    const categoryObj = categoryMapping.find(
      (c) => c.category === categoryName,
    );

    setRaiseIssueFormData((prev) => ({
      ...prev,
      issue_category: categoryName,
      issue_subcategory: "",
    }));
    setSubCategoryOptions(categoryObj ? categoryObj.sub_categories : []);
  };

  // Fetch order details to get IGM data
  const handleOpenRaiseIssueModal = useCallback(
    async (currentOrder) => {
      // Check if user has IGM service
      if (!hasService(6)) {
        addToast("KINDLY RAISE ISSUE THROUGH IGM!", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      if (!currentOrder?.order_id) return;

      setFetchingOrderData(true);
      try {
        const result = await apiHandler({
          url: `${endpoint.GET_ORDER_DETAIL}${currentOrder.order_id}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        const data = result?.data?.data || result?.data;
        if (data) {
          setOrderData(data);

          // Pre-fill the form with order data
          //console.log("domain:", data.domain);
          let domainCode = "";
          const issueDomains = data?.igmData?.issue_domain || [];

          if (data.domain && issueDomains.length > 0) {
            const domainObj = issueDomains.find(
              (d) => d.name === data.domain
            );
            domainCode = domainObj ? domainObj.code : "";
          }


          setRaiseIssueFormData({
            domain: domainCode || "",
            issue_category: "",
            issue_subcategory: "",
            short_desc: "",
            detailed_desc: "",
            refs: [
              {
                ref_type: "PAYMENT",
                ref_id: data?.payment_details?.payment_id || "",
              },
            ],
            images: [],
            city: "",
            bap_id: data.buyer_app_details?.bap_id || "",
            bap_uri: data.buyer_app_details?.bap_uri || "",
          });
        }
        setShowIssueModal(true);
      } catch (error) {
        //console.error("Error fetching order details:", error);
        addToast("Failed to fetch order details", { appearance: "error" });
      } finally {
        setFetchingOrderData(false);
      }
    },
    [authData, dispatch, storeId, addToast, orderData?.igmData?.issue_domain, hasService],
  );

  // Reference change handler
  const handleRefChange = (index, field, value) => {
    const newRefs = [...raiseIssueFormData.refs];
    newRefs[index][field] = value;
    setRaiseIssueFormData((prev) => ({ ...prev, refs: newRefs }));
  };

  // Add reference row
  const addRefRow = () => {
    setRaiseIssueFormData((prev) => ({
      ...prev,
      refs: [...prev.refs, { ref_type: "", ref_id: "" }],
    }));
  };

  // Remove reference row
  const removeRefRow = (index) => {
    setRaiseIssueFormData((prev) => ({
      ...prev,
      refs: prev.refs.filter((_, i) => i !== index),
    }));
  };

  // File change handler
  const handleFileChange = (e) => {
    if (e.target.files) {
      setRaiseIssueFormData((prev) => ({
        ...prev,
        images: Array.from(e.target.files),
      }));
    }
  };

  // Form submission handler
  const handleRaiseIssueFormSubmit = useCallback(
    async (e) => {
      if (e) {
        e.preventDefault();
      }

      // 1. Validation
      // Basic required fields (city validated separately)
      if (
        !raiseIssueFormData.domain ||
        !raiseIssueFormData.issue_category ||
        !raiseIssueFormData.issue_subcategory ||
        !raiseIssueFormData.short_desc
      ) {
        addToast("Please fill all required fields", { appearance: "warning" });
        return;
      }

      // City is required
      if (!raiseIssueFormData.city) {
        addToast("Please fill all required fields", { appearance: "warning" });
        return;
      }

      // City STD code format: should start with `std:` followed by digits
      // Digits must start with 0 and be between 3 and 5 digits (including the leading 0)
      // Examples: `std:012`, `std:01234`
      const cityPattern = /^std:0\d{2,4}$/;
      if (!cityPattern.test(raiseIssueFormData.city)) {
        addToast(
          "City STD code must be in format 'std:0XX' or 'std:0XXX' or 'std:0XXXX' where X is a digit",
          {
            appearance: "warning",
          },
        );
        return;
      }

      try {
        setIsSubmittingForm(true);

        // Assembly multi-part form data for images
        const formData = new FormData();

        // Basic fields
        formData.append("domain", raiseIssueFormData.domain);
        formData.append("issue_category", raiseIssueFormData.issue_category);
        formData.append(
          "issue_subcategory",
          raiseIssueFormData.issue_subcategory,
        );
        formData.append("short_desc", raiseIssueFormData.short_desc);
        formData.append("detailed_desc", raiseIssueFormData.detailed_desc);
        formData.append("city", raiseIssueFormData.city);
        formData.append("bap_id", raiseIssueFormData.bap_id);
        formData.append("bap_uri", raiseIssueFormData.bap_uri);

        // References needs to be stringified
        formData.append("refs", JSON.stringify(raiseIssueFormData.refs));

        // Images
        if (raiseIssueFormData.images && raiseIssueFormData.images.length > 0) {
          raiseIssueFormData.images.forEach((file) => {
            formData.append("images", file);
          });
        }

        const response = await apiHandler({
          url: endpoint.RAISE_ISSUE,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          authToken: authData,
          dispatch,
          storeId,
        });

        // if (response?.data?.result?.success || response?.status === 200) {
        //   addToast("Issue raised successfully", { appearance: "success" });
        //   setShowIssueModal(false);

        //   // Reset form
        //   setRaiseIssueFormData({
        //     domain: "",
        //     issue_category: "",
        //     issue_subcategory: "",
        //     bap_id: userData?.user_id || "",
        //     city: "",
        //     bap_uri: userData?.subscriber_id || "",
        //     refs: [{ ref_type: "PAYMENT", ref_id: "" }],
        //     short_desc: "",
        //     detailed_desc: "",
        //     images: [],
        //   });
        // }

        const result = response?.data?.result;
        const isSuccess = result?.success === true;
        const message = result?.message;
        // COMPREHENSIVE DEBUGGING - ADD ALL THESE
        //console.log("=== FULL RESPONSE DEBUG ===");
        //console.log("response:", response);
        //console.log("response.data:", response?.data);
        //console.log("response.data.result:", response?.data?.result);
        //console.log("response.status:", response?.status);
        //console.log("response.data.status:", response?.data?.status);
        //console.log("response.data.code:", response?.data?.code);
        //console.log("response.data.message:", response?.data?.message);
        //console.log("=========================");

        if (isSuccess) {
          addToast(message || "Issue raised successfully", {
            appearance: "success",
          });
          setShowIssueModal(false);
          //console.log("domain:", userData.domain);
          let domainCode = "";
          const issueDomains = userData?.igmData?.issue_domain || [];
          if (userData.domain && issueDomains.length > 0) {
            const domainObj = issueDomains.find(
              (d) => d.name === userData.domain
            );
            domainCode = domainObj ? domainObj.code : "";
            //console.log("Mapped domain code:", domainCode);
          }


          // Reset form
          setRaiseIssueFormData({
            domain: domainCode || "",
            issue_category: "",
            issue_subcategory: "",
            bap_id: userData?.user_id || "",
            city: "",
            bap_uri: userData?.subscriber_id || "",
            refs: [{ ref_type: "PAYMENT", ref_id: "" }],
            short_desc: "",
            detailed_desc: "",
            images: [],
          });
        } else {
          addToast(message || "Failed to raise issue", {
            appearance: "error",
          });
        }
      } catch (error) {
        //console.error("Error raising issue:", error);

        const errorMessage =
          error.response?.data?.result?.message ||
          error.response?.data?.message ||
          error.message ||
          "Failed to raise issue";

        addToast(errorMessage, {
          appearance: "error",
        });
      } finally {
        setIsSubmittingForm(false);
      }
    },
    [raiseIssueFormData, authData, storeId, dispatch, addToast, userData, orderData?.igmData?.issue_domain],
  );

  return {
    transactionData,
    loading,
    error,
    activeTab,
    activeOrderIndex,
    activeMessageTab,
    handleLatestSettlement,
    handleTabClick: setActiveTab,
    handleOrderClick: (index) => {
      setActiveOrderIndex(index);
      setActiveMessageTab(0);
    },
    handleMessageTabClick: setActiveMessageTab,
    handleRaiseRecon,

    // Export old modal properties
    showIssueModal,
    setShowIssueModal,
    isSubmittingIssue,
    paymentId,
    setPaymentId,
    toSubscriberId,
    setToSubscriberId,
    toSubscriberUri,
    setToSubscriberUri,
    handleOpenIssueModal,
    handleCloseIssueModal,
    handleRaiseIssueSubmit,
    handleGoBack,

    showSendReconModal,
    handleOpenSendReconModal,
    handleCloseSendReconModal,
    handleSendReconFieldChange,
    handleSendReconOrderChange,
    handleSendReconSubmit,
    isSubmittingSendRecon,
    sendReconForm,
    settlementStatusOptions,

    // Export new raise issue form properties
    orderData,
    fetchingOrderData,
    isSubmittingForm,
    raiseIssueFormData,
    setRaiseIssueFormData,
    subCategoryOptions,
    handleFormChange,
    handleCategoryChange,
    handleRefChange,
    addRefRow,
    removeRefRow,
    handleFileChange,
    handleOpenRaiseIssueModal,
    handleRaiseIssueFormSubmit,
  };
};
